import React from 'react';
import { Target, Droplets, Utensils } from 'lucide-react';

interface ProgressRingProps {
  value: number;
  max: number;
  size: number;
  strokeWidth: number;
  color: string;
  backgroundColor?: string;
}

const ProgressRing: React.FC<ProgressRingProps> = ({
  value,
  max,
  size,
  strokeWidth,
  color,
  backgroundColor = 'rgba(0,0,0,0.1)'
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const progress = Math.min(value / max, 1);
  const strokeDasharray = `${progress * circumference} ${circumference}`;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        className="transform -rotate-90"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={backgroundColor}
          strokeWidth={strokeWidth}
          fill="transparent"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth={strokeWidth}
          fill="transparent"
          strokeDasharray={strokeDasharray}
          strokeLinecap="round"
          className="transition-all duration-500 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold" style={{ color: 'var(--color-text)' }}>
          {Math.round(progress * 100)}%
        </span>
        <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
          {value}/{max}
        </span>
      </div>
    </div>
  );
};

interface MetricCardProps {
  title: string;
  icon: React.ReactNode;
  value: number;
  max: number;
  unit: string;
  color: string;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  icon,
  value,
  max,
  unit,
  color
}) => {
  return (
    <div 
      className="bg-white rounded-xl p-6 flex flex-col items-center space-y-4"
      style={{ boxShadow: 'var(--shadow-lg)' }}
    >
      <div className="flex items-center space-x-2">
        {icon}
        <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text)' }}>
          {title}
        </h3>
      </div>
      
      <ProgressRing
        value={value}
        max={max}
        size={120}
        strokeWidth={8}
        color={color}
      />
      
      <div className="text-center">
        <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
          {value} {unit} of {max} {unit}
        </p>
        <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
          {value >= max ? 'Goal achieved!' : `${max - value} ${unit} remaining`}
        </p>
      </div>
    </div>
  );
};

const ProgressRingsPanel: React.FC = () => {
  const metrics = [
    {
      title: 'Workouts',
      icon: <Target className="h-6 w-6" style={{ color: 'var(--color-primary)' }} />,
      value: 4,
      max: 5,
      unit: 'sessions',
      color: 'var(--color-primary)'
    },
    {
      title: 'Protein',
      icon: <Utensils className="h-6 w-6" style={{ color: 'var(--color-accent)' }} />,
      value: 85,
      max: 120,
      unit: 'g',
      color: 'var(--color-accent)'
    },
    {
      title: 'Hydration',
      icon: <Droplets className="h-6 w-6" style={{ color: 'var(--color-secondary)' }} />,
      value: 6,
      max: 8,
      unit: 'glasses',
      color: 'var(--color-secondary)'
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold" style={{ color: 'var(--color-text)' }}>
          Today's Progress
        </h2>
        <div className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
          {new Date().toLocaleDateString('en-US', { 
            weekday: 'long', 
            month: 'short', 
            day: 'numeric' 
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {metrics.map((metric, index) => (
          <MetricCard
            key={index}
            title={metric.title}
            icon={metric.icon}
            value={metric.value}
            max={metric.max}
            unit={metric.unit}
            color={metric.color}
          />
        ))}
      </div>

      <div 
        className="bg-white rounded-xl p-6"
        style={{ boxShadow: 'var(--shadow-lg)' }}
      >
        <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text)' }}>
          Weekly Overview
        </h3>
        
        <div className="grid grid-cols-7 gap-2">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => {
            const completed = index < 4;
            return (
              <div key={day} className="text-center">
                <div className="text-xs mb-2" style={{ color: 'var(--color-text-muted)' }}>
                  {day}
                </div>
                <div 
                  className="w-8 h-8 rounded-full mx-auto flex items-center justify-center"
                  style={{
                    background: completed 
                      ? 'var(--gradient-primary)' 
                      : 'rgba(0,0,0,0.1)',
                    color: completed ? 'var(--color-white)' : 'var(--color-text-muted)'
                  }}
                >
                  {completed ? '✓' : index + 1}
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="mt-4 text-center">
          <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
            4 of 5 workouts completed this week
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProgressRingsPanel;
